import type { GetMyJoinRequestsResponse } from '@orbit/api-types'
import { buildDateRange } from '@components/TripOverview/core/overviewShared'
import { countLabel } from '@/utils/plural'
import { capitalizedFirstName } from '@/utils/userNames'

/**
 * How Join requests read on the web: the owner's request rows, the requester's
 * "Waiting for approval" cards, and the lines the Join page shares with them.
 * Ported from iOS (`JoinRequestRowPresentation`, `JoinRequestCardPresentation`,
 * `TripJoinPresentation`). Pure, so it is tested in node.
 */

type MyJoinRequest = GetMyJoinRequestsResponse['requests'][number]

const MINUTE = 60
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** The last day of `month` (0-based) in `year`, in the viewer's calendar. */
const daysInMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate()

/** `date` moved by whole calendar months, its day clamped to the target month's length. */
const addMonths = (date: Date, months: number): Date => {
	const target = new Date(date)
	target.setDate(1)
	target.setMonth(target.getMonth() + months)
	target.setDate(Math.min(date.getDate(), daysInMonth(target.getFullYear(), target.getMonth())))
	return target
}

/** Whole calendar months from `from` to `to` (`to` is not before `from`). */
const wholeMonthsBetween = (from: Date, to: Date): number => {
	let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
	if (months > 0 && addMonths(from, months) > to) months -= 1
	return months
}

const ago = (count: number, unit: string): string => `${countLabel(count, unit)} ago`

/**
 * "Asked 2 hours ago" for a request's Unix-seconds `requestedAt`: the largest
 * non-zero calendar unit, truncated, as iOS's `RelativeDateTimeFormatter`
 * (`.full`) says it. "Asked just now" inside the first minute, or for a clock
 * running behind the server's.
 */
export const askedAgo = (requestedAt: number, now: number = Date.now()): string => {
	const seconds = now / 1000 - requestedAt
	if (!(seconds >= MINUTE)) return 'Asked just now'

	const requested = new Date(requestedAt * 1000)
	const months = wholeMonthsBetween(requested, new Date(now))
	if (months >= 12) return `Asked ${ago(Math.floor(months / 12), 'year')}`
	if (months >= 1) return `Asked ${ago(months, 'month')}`

	const days = Math.floor(seconds / DAY)
	if (days >= 7) return `Asked ${ago(Math.floor(days / 7), 'week')}`
	if (days >= 1) return `Asked ${ago(days, 'day')}`

	const hours = Math.floor(seconds / HOUR)
	if (hours >= 1) return `Asked ${ago(hours, 'hour')}`
	return `Asked ${ago(Math.floor(seconds / MINUTE), 'minute')}`
}

/**
 * "Apr 15 – Apr 17, 2026 · 3 days" for a trip the visitor isn't in yet — the
 * dates and the length, each dropped when the trip doesn't have it yet; null
 * when it has neither.
 */
export const tripPreviewMeta = (startDate: string | null, dayCount: number): string | null => {
	const parts = [buildDateRange(startDate, dayCount), dayCount > 0 ? countLabel(dayCount, 'day') : null].filter(
		(part): part is string => !!part,
	)
	return parts.length > 0 ? parts.join(' · ') : null
}

/** The owner as a line about my request opens with them: their first name, or "The owner". */
const ownerSubject = (ownerFirstName: string): string => capitalizedFirstName(ownerFirstName) ?? 'The owner'


/** One of my requests as its card shows it. */
export interface MyJoinRequestCard {
	code: string
	tripName: string
	/** "Apr 15 – Apr 17, 2026 · 3 days", or null when the trip has neither yet. */
	meta: string | null
	/** Who turned the request down. Null while it's pending: "Requested" says it all. */
	detail: string | null
	isPending: boolean
	status: 'Requested' | 'Not approved'
	/** Pending is cancelled; a denial is dismissed. Both remove the card. */
	actionLabel: 'Cancel' | 'Dismiss'
	/** The action's accessible name, naming the trip. */
	actionAriaLabel: string
}

export const myJoinRequestCard = (request: MyJoinRequest): MyJoinRequestCard => {
	const { trip } = request
	const isPending = request.status === 'Pending'
	return {
		code: trip.code,
		tripName: trip.name,
		meta: tripPreviewMeta(trip.startDate, trip.dayCount),
		detail: isPending ? null : `${ownerSubject(trip.owner.firstName)} didn't approve your request`,
		isPending,
		status: isPending ? 'Requested' : 'Not approved',
		actionLabel: isPending ? 'Cancel' : 'Dismiss',
		actionAriaLabel: `${isPending ? 'Cancel' : 'Dismiss'} request to join ${trip.name}`,
	}
}

/**
 * My requests as the trips list shows them, leaving out any for a trip I'm
 * already in — approved on another device before this list refreshed (iOS does the same).
 */
export const myJoinRequestCards = (requests: MyJoinRequest[], myTrips: { id: number }[]): MyJoinRequestCard[] => {
	const mine = new Set(myTrips.map(trip => trip.id))
	return requests.filter(request => !mine.has(request.trip.id)).map(myJoinRequestCard)
}

/**
 * The line under the Require Join Approval switch (iOS `JoinApprovalSetting`).
 * Only the owner can flip it; everyone else sees why they can't.
 */
export const joinApprovalExplanation = ({ isRequired, isEditable }: { isRequired: boolean; isEditable: boolean }): string => {
	if (!isEditable) return 'Only the owner can change this'
	return isRequired
		? 'You will need to approve join requests'
		: 'Anyone with the link or code joins straight away.'
}
